<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleClose">
    <div class="modal-content" @click.stop>
      <h2 class="modal-title">Grant Usage Minutes</h2>
      <div class="line-divider"></div>

      <div class="modal-form">
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <p v-if="successMessage" class="success-message">
          {{ successMessage }}
        </p>

        <!-- Phase 1: Check User Status -->
        <div v-if="!userUsageStatus" class="phase-section">
          <div class="form-group">
            <label for="user-email">Email *</label>
            <input
              type="email"
              id="user-email"
              v-model="email"
              placeholder="Enter user email"
              required
              @keyup.enter="checkUserStatus"
            />
            <small class="form-hint">Enter the user's email address</small>
          </div>

          <div class="button-group">
            <button
              type="button"
              class="modal-submit"
              @click="checkUserStatus"
              :disabled="isLoading || !email.trim()"
            >
              <div class="button-content">
                <div v-if="isLoading" class="spinner"></div>
                <span>{{ isLoading ? "Checking..." : "Check Status" }}</span>
              </div>
            </button>
            <button
              type="button"
              class="modal-cancel"
              @click="handleClose"
              :disabled="isLoading"
            >
              Cancel
            </button>
          </div>
        </div>

        <!-- Phase 2: Show Usage Details and Grant Form -->
        <div v-else class="phase-section">
          <!-- User Information -->
          <div class="usage-details-card">
            <h3 class="details-title">User Information</h3>
            <div class="details-grid">
              <div v-if="userData?.name" class="detail-item">
                <span class="detail-label">Name:</span>
                <span class="detail-value">{{ userData.name }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Email:</span>
                <span class="detail-value">{{ userData?.email || email }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">User ID:</span>
                <span class="detail-value">{{ userId }}</span>
              </div>
              <div v-if="userData?.createdAt" class="detail-item">
                <span class="detail-label">Account Created At:</span>
                <span class="detail-value">{{
                  formatCreatedAt(userData.createdAt)
                }}</span>
              </div>
            </div>
            <br />
            <br />
            <h3 class="details-title">Current Usage Status</h3>
            <div v-if="hasUsage" class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Plan:</span>
                <span class="detail-value">{{
                  userUsageStatus.data.planName
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Status:</span>
                <span
                  class="detail-value"
                  :class="'status-' + userUsageStatus.data.status"
                >
                  {{ userUsageStatus.data.status }}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Remaining Minutes:</span>
                <span class="detail-value highlight">{{
                  userUsageStatus.data.remainingMinutes
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Total Minutes:</span>
                <span class="detail-value">{{
                  userUsageStatus.data.totalMinutesAllowed
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Plan Start Date:</span>
                <span class="detail-value">{{
                  formatDate(userUsageStatus.data.billingPeriodStart)
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Plan End Date:</span>
                <span class="detail-value">{{
                  formatDate(userUsageStatus.data.billingPeriodEnd)
                }}</span>
              </div>
            </div>
            <div v-else class="no-usage-message">
              <p>{{ userUsageStatus.message }}</p>
              <small
                >A new usage record will be created when you grant
                minutes.</small
              >
            </div>
          </div>

          <!-- Grant Form -->
          <form @submit.prevent="handleSubmit" class="grant-form">
            <div class="form-group">
              <div class="slider-label-wrapper">
                <label for="minutes">Minutes to Grant *</label>
                <span class="slider-value">{{ formData.minutes || 0 }}</span>
              </div>
              <input
                type="range"
                id="minutes"
                v-model.number="formData.minutes"
                class="slider"
                min="1"
                :max="maxMinutesAllowed"
                step="1"
                :disabled="maxMinutesAllowed === 0"
                @input="updateSliderValue"
              />
              <small class="form-hint">
                <span v-if="maxMinutesAllowed === 0">
                  Cannot grant minutes - user has full allocation
                </span>
                <span v-else>
                  Slide to select minutes (Max: {{ maxMinutesAllowed }} minutes)
                </span>
              </small>
            </div>

            <div class="form-group">
              <label for="reason">Reason *</label>
              <textarea
                id="reason"
                v-model="formData.reason"
                placeholder="e.g., Compensation for downtime, Welcome bonus, etc."
                rows="3"
                required
              ></textarea>
              <small class="form-hint">Provide a reason for this grant</small>
            </div>

            <div class="button-group">
              <button
                type="submit"
                class="modal-submit"
                :disabled="isLoading || !isFormValid"
              >
                <div class="button-content">
                  <div v-if="isLoading" class="spinner"></div>
                  <span>{{ isLoading ? "Granting..." : "Grant Minutes" }}</span>
                </div>
              </button>
              <button
                type="button"
                class="modal-cancel"
                @click="resetToPhaseOne"
                :disabled="isLoading"
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usageService } from "@/services/usageService";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

export default {
  name: "GrantUsageModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      email: "",
      userId: null, // Resolved userId from email lookup
      userData: null, // Full user data from Firestore
      userUsageStatus: null,
      formData: {
        minutes: null,
        reason: "",
      },
      isLoading: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    hasUsage() {
      return this.userUsageStatus?.data?.hasUsage === true;
    },
    maxMinutesAllowed() {
      if (!this.userUsageStatus) return 0;

      if (this.hasUsage) {
        // Case A: Has existing usage - can only grant up to consumed minutes
        const totalAllowed = this.userUsageStatus.data.totalMinutesAllowed || 0;
        const remaining = this.userUsageStatus.data.remainingMinutes || 0;
        return totalAllowed - remaining;
      } else {
        // Case B: No usage - cap at 300 minutes
        return 300;
      }
    },
    isFormValid() {
      return (
        this.formData.minutes &&
        this.formData.minutes >= 1 &&
        this.formData.minutes <= this.maxMinutesAllowed &&
        this.formData.reason &&
        this.formData.reason.trim().length > 0
      );
    },
  },
  methods: {
    updateSliderValue(event) {
      this.formData.minutes = parseInt(event.target.value);
    },
    handleClose() {
      if (!this.isLoading) {
        this.clearForm();
        this.$emit("close");
      }
    },
    clearForm() {
      this.email = "";
      this.userId = null;
      this.userData = null;
      this.userUsageStatus = null;
      this.formData = {
        minutes: null,
        reason: "",
      };
      this.isLoading = false;
      this.errorMessage = "";
      this.successMessage = "";
    },
    resetToPhaseOne() {
      this.userUsageStatus = null;
      this.userId = null;
      this.userData = null;
      this.formData = {
        minutes: null,
        reason: "",
      };
      this.errorMessage = "";
      this.successMessage = "";
    },
    async lookupUserIdByEmail(email) {
      try {
        const db = getFirestore();
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          where("email", "==", email.trim().toLowerCase())
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          throw new Error("No user found with this email address");
        }

        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();

        if (!userData.userId) {
          throw new Error("User document does not have a userId field");
        }

        return { userId: userData.userId, userData };
      } catch (error) {
        console.error("Error looking up user by email:", error);
        throw error;
      }
    },
    async checkUserStatus() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.email.trim()) {
        this.errorMessage = "Please enter an email address";
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email.trim())) {
        this.errorMessage = "Please enter a valid email address";
        return;
      }

      this.isLoading = true;

      try {
        // First, lookup userId from email
        const { userId: resolvedUserId, userData } =
          await this.lookupUserIdByEmail(this.email);
        this.userId = resolvedUserId;
        this.userData = userData;

        // Then, check user status with the resolved userId
        const result = await usageService.getUserUsageStatus(resolvedUserId);
        this.userUsageStatus = result;

        // Set default slider value to half of max allowed (minimum 1)
        if (this.maxMinutesAllowed > 0) {
          this.formData.minutes = Math.max(
            1,
            Math.floor(this.maxMinutesAllowed / 2)
          );
        }
      } catch (error) {
        console.error("Error checking user status:", error);
        this.errorMessage =
          error.message || "Failed to fetch user status. Please try again.";
        this.userId = null; // Reset userId on error
        this.userData = null; // Reset userData on error
      } finally {
        this.isLoading = false;
      }
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.successMessage = "";

      // Validation
      if (!this.formData.minutes || this.formData.minutes < 1) {
        this.errorMessage =
          "Please enter a valid number of minutes (minimum 1)";
        return;
      }

      if (this.formData.minutes > this.maxMinutesAllowed) {
        this.errorMessage = this.hasUsage
          ? `Cannot grant more than ${this.maxMinutesAllowed} minutes (only consumed minutes can be restored)`
          : `Cannot grant more than ${this.maxMinutesAllowed} minutes for first-time grants`;
        return;
      }

      if (!this.formData.reason.trim()) {
        this.errorMessage = "Please provide a reason for this grant";
        return;
      }

      if (!this.userId) {
        this.errorMessage =
          "User ID not found. Please check the email and try again.";
        return;
      }

      this.isLoading = true;

      try {
        // Build payload
        const payload = {
          userId: this.userId,
          minutes: this.formData.minutes,
          reason: this.formData.reason.trim(),
        };

        // Add usageId if user has existing usage
        if (this.hasUsage && this.userUsageStatus.data.usageId) {
          payload.usageId = this.userUsageStatus.data.usageId;
        }

        console.log("payload", payload);

        const result = await usageService.grantUsageMinutes(payload);

        this.successMessage = result.message || "Minutes granted successfully!";

        // Emit success event
        this.$emit("usage-granted", result);

        // Close modal after short delay
        setTimeout(() => {
          this.handleClose();
        }, 1500);
      } catch (error) {
        console.error("Error granting minutes:", error);
        this.errorMessage =
          error.message || "Failed to grant minutes. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateValue) {
      if (!dateValue) return "N/A";
      try {
        let date;

        // Handle Firestore Timestamp object from API (has _seconds property)
        if (dateValue._seconds !== undefined) {
          date = new Date(dateValue._seconds * 1000);
        }
        // Handle regular Date or ISO string
        else {
          date = new Date(dateValue);
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
          return "N/A";
        }

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        console.error("Error formatting date:", error);
        return "N/A";
      }
    },
    formatCreatedAt(dateValue) {
      if (!dateValue) return "N/A";
      try {
        let date;

        // Handle Firestore Timestamp object (has _seconds property)
        if (dateValue._seconds !== undefined) {
          date = new Date(dateValue._seconds * 1000);
        }
        // Handle Firestore Timestamp with toDate method
        else if (dateValue.toDate && typeof dateValue.toDate === "function") {
          date = dateValue.toDate();
        }
        // Handle regular Date or ISO string
        else {
          date = new Date(dateValue);
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
          return "N/A";
        }

        const day = date.getDate();
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        return `${day}, ${month}, ${year}`;
      } catch (error) {
        console.error("Error formatting createdAt date:", error);
        return "N/A";
      }
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.clearForm();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 1.5rem 0 1.5rem;
  color: #111827;
}

.line-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 1.5rem;
}

.modal-form {
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.phase-section {
  width: 100%;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

.success-message {
  color: #10b981;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
}

.usage-details-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.details-title {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.detail-value {
  font-size: 0.875rem;
  color: #111827;
  font-weight: 500;
}

.detail-value.highlight {
  color: #2563eb;
  font-size: 1rem;
  font-weight: 600;
}

.detail-value.status-active {
  color: #10b981;
  text-transform: capitalize;
}

.detail-value.status-cancelled {
  color: #ef4444;
  text-transform: capitalize;
}

.no-usage-message {
  text-align: left;
  padding: 0rem;
}

.no-usage-message p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.no-usage-message small {
  font-size: 0.75rem;
  color: #9ca3af;
}

.grant-form {
  width: 100%;
}

.form-group {
  margin-bottom: 1.25rem;
  text-align: left;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #000;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.form-hint {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.slider-label-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.slider-value {
  font-size: 0.875rem;
  color: #111827;
  font-weight: 500;
}

.button-group {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.modal-submit {
  flex: 1;
  background-color: #000;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.modal-submit:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.modal-submit:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.modal-cancel {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.modal-cancel:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.modal-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .button-group {
    flex-direction: column;
  }

  .modal-submit,
  .modal-cancel {
    width: 100%;
  }
}
</style>
